import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { Metric } from '../../components/Metric'
import { visitorsApi } from '../../api/visitors'
import { fmtDateTime, PUBLICATION_NAMES } from '../../lib/format'
import { duration, fromEmailLine, STEP_LABEL } from '../../lib/visitorsFormat'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { useMemberAccess } from './engagementBits'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, th } from './VisitorsFrame'

const TYPE_TONE: Record<string, string> = {
  page_view: 'text-radar-cyan',
  cta_click: 'text-amber-glow',
  checkout_step: 'text-radar-violet',
  purchase: 'text-radar-green',
}

function propsSummary(p: Record<string, unknown>): string {
  const s = Object.entries(p ?? {}).map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`).join(' · ')
  return s.length > 140 ? `${s.slice(0, 139)}…` : s
}

export function VisitorsSession() {
  const { pub = '', sessionId = '' } = useParams()
  const { search } = useVisitorsRange()
  const { canMember } = useMemberAccess()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const q = useQuery({
    queryKey: ['visitors-session', sessionId],
    queryFn: () => visitorsApi.session(sessionId),
    enabled: okPub && sessionId !== '',
  })
  const base = `/visitors/${pub}`
  const d = q.data
  const span = d ? `${fmtDateTime(d.started_at)} – ${fmtDateTime(d.ended_at)}` : ''

  return (
    <VisitorsFrame
      pub={pub}
      valid={okPub}
      title={d ? `Visit · ${span}` : 'Visit'}
      crumbs={[{ label: 'Visits', to: `${base}/sessions?${search}` }, { label: 'Visit' }]}
    >
      {q.isError ? (
        <QueryProblem error={q.error} pub={pub} />
      ) : !d ? (
        <Skeleton h="h-64" />
      ) : (
        <>
          <p className="font-mono text-[12px] text-paper-dim break-words">
            session {d.session_id} · visitor{' '}
            <Link to={`${base}/visitors/${encodeURIComponent(d.visitor_id)}?${search}`} className={linkCls}>{d.visitor_id}</Link> · member{' '}
            {d.subscriber_id != null
              ? canMember ? <Link to={`${base}/members/${d.subscriber_id}?${search}`} className={linkCls}>#{d.subscriber_id}</Link> : `#${d.subscriber_id}`
              : '—'} ·{' '}
            {d.channel ?? '—'} · {d.device ?? '—'} · {d.country ?? '—'} · {d.verdict}
            {d.verdict_reason ? ` (${d.verdict_reason})` : ''}
          </p>
          {d.from_email && (
            <p className="font-mono text-[12px] text-paper break-words">
              <span className="label mr-2">From email</span>{fromEmailLine(d.from_email)}
            </p>
          )}
          <Panel accent="amber">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Metric size="sm" label="Duration" value={duration(Math.max(0, new Date(d.ended_at).getTime() - new Date(d.started_at).getTime()))} />
              <Metric size="sm" label="Engaged time" value={duration(d.engaged_ms)} />
              <Metric size="sm" label="Pages" value={d.page_count} />
              <Metric size="sm" label="Events" value={d.events.length} />
              <Metric
                size="sm"
                tone={d.max_funnel_step === 'paid' ? 'green' : 'paper'}
                label="Outcome"
                value={d.max_funnel_step ? STEP_LABEL[d.max_funnel_step] : '—'}
                sub={d.order_id != null ? `order #${d.order_id}` : undefined}
              />
            </div>
          </Panel>
          <Panel label="Timeline" accent="cyan" bodyClassName="p-0">
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-[12px] text-paper">
                <thead>
                  <tr className="border-b border-ink-500">
                    {['Time', 'Type', 'Path', 'Details'].map((h) => <th key={h} className={th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {d.events.map((e) => (
                    <tr key={e.id} className="border-b border-ink-500/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Link to={`${base}/events/${e.id}?${search}`} className={linkCls}>{fmtDateTime(e.ts)}</Link>
                      </td>
                      <td className={`px-3 py-2 ${TYPE_TONE[e.type] ?? 'text-paper-dim'}`}>{e.type}</td>
                      <td className="px-3 py-2 max-w-[320px] truncate" title={e.path}>{e.path}</td>
                      <td className="px-3 py-2 text-paper-dim">{propsSummary(e.props)}</td>
                    </tr>
                  ))}
                  {d.events.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-paper-faint">No events recorded for this visit</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </VisitorsFrame>
  )
}
